import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'

/**
 * LoadingSpinner — spinner circulaire DS
 *
 * Remplace les 56 implementations DIY `border-2 border-violet-500 border-t-transparent animate-spin`
 * dispersees dans l'app. Uniformise taille, couleur, a11y.
 *
 * Usage :
 *   <LoadingSpinner />                       // md, default (brand)
 *   <LoadingSpinner size="sm" />             // petit, dans un bouton
 *   <LoadingSpinner size="lg" intent="muted" />  // gros, au milieu d'une page
 *   <LoadingSpinner label="Chargement du dossier..." />  // avec texte a cote
 *
 * A11y : role="status" + aria-label par defaut "Chargement en cours".
 * Pour un loader page entiere, utiliser <PageLoader> qui centre + ajoute un message.
 */

const spinnerVariants = cva(
  'inline-block rounded-full border-solid border-t-transparent animate-spin',
  {
    variants: {
      size: {
        xs: 'w-3 h-3 border-[1.5px]',
        sm: 'w-4 h-4 border-2',
        md: 'w-6 h-6 border-2',
        lg: 'w-8 h-8 border-[3px]',
        xl: 'w-10 h-10 border-[3px]',
      },
      intent: {
        /** Couleur primaire brand (violet). Defaut. */
        brand: 'border-primary',
        /** Gris discret — pour les loaders inline dans un bouton ou un picker. */
        muted: 'border-ink-ghost',
        /** Sur fond sombre ou bouton primaire : blanc translucide. */
        inverse: 'border-white/80',
        /** Couleur success — pour loaders contextuels "enregistrement OK en cours". */
        success: 'border-success',
        /** Couleur warning. */
        warning: 'border-warning',
        /** Couleur error. */
        error: 'border-error',
      },
    },
    defaultVariants: { size: 'md', intent: 'brand' },
  }
)

export interface LoadingSpinnerProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'>,
    VariantProps<typeof spinnerVariants> {
  /** Label optionnel affiche a droite du spinner. Si absent, un aria-label est quand meme ajoute. */
  label?: React.ReactNode
  /** aria-label pour les lecteurs d'ecran si aucun label visible. Defaut : "Chargement en cours". */
  srLabel?: string
}

const LoadingSpinner = React.forwardRef<HTMLSpanElement, LoadingSpinnerProps>(
  ({ className, size, intent, label, srLabel = 'Chargement en cours', ...props }, ref) => {
    return (
      <span
        ref={ref}
        role="status"
        aria-live="polite"
        className={cn('inline-flex items-center gap-2', className)}
        {...props}
      >
        <span className={spinnerVariants({ size, intent })} aria-hidden="true" />
        {label ? (
          <span className="text-data text-ink-secondary">{label}</span>
        ) : (
          <span className="sr-only">{srLabel}</span>
        )}
      </span>
    )
  }
)
LoadingSpinner.displayName = 'LoadingSpinner'

// ─────────────────────────────────────────────────────────────────────
// PageLoader — loader centre plein ecran / plein conteneur
// ─────────────────────────────────────────────────────────────────────

export interface PageLoaderProps {
  /** Message sous le spinner. Optionnel. */
  message?: React.ReactNode
  /** Taille du spinner. Defaut : lg. */
  size?: LoadingSpinnerProps['size']
  /** Couleur du spinner. Defaut : brand. */
  intent?: LoadingSpinnerProps['intent']
  /** Hauteur du conteneur. Defaut : "min-h-[240px]". Passer "full" pour 100vh. */
  height?: 'sm' | 'md' | 'lg' | 'full'
  className?: string
}

/**
 * PageLoader — centre un LoadingSpinner + message dans un conteneur.
 * Remplace les patterns `<div className="flex items-center justify-center py-16">...</div>`.
 *
 * Usage :
 *   if (loading) return <PageLoader message="Chargement des clients..." />
 *   if (loading) return <PageLoader height="full" />  // plein ecran
 */
const pageLoaderHeights = {
  sm: 'min-h-[120px]',
  md: 'min-h-[240px]',
  lg: 'min-h-[400px]',
  full: 'min-h-screen',
}

export function PageLoader({
  message,
  size = 'lg',
  intent = 'brand',
  height = 'md',
  className,
}: PageLoaderProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex flex-col items-center justify-center gap-3',
        pageLoaderHeights[height],
        className
      )}
    >
      <span className={spinnerVariants({ size, intent })} aria-hidden="true" />
      {message ? (
        <p className="text-data text-ink-muted">{message}</p>
      ) : (
        <span className="sr-only">Chargement en cours</span>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// ButtonSpinner — spinner optimise pour un bouton (xs + inverse)
// ─────────────────────────────────────────────────────────────────────

export interface ButtonSpinnerProps {
  /** Par defaut inverse (blanc) pour un Button primaire. Utiliser "muted" pour un Button outline. */
  intent?: 'inverse' | 'muted' | 'brand'
  /** Defaut xs. */
  size?: 'xs' | 'sm'
  className?: string
}

/**
 * ButtonSpinner — spinner inline pour un <Button>.
 * Optimise pour les cas "Sauvegarde...", "Envoi...", "Validation...".
 *
 * Usage :
 *   <Button disabled={saving}>
 *     {saving && <ButtonSpinner />}
 *     {saving ? 'Sauvegarde...' : 'Sauvegarder'}
 *   </Button>
 */
export function ButtonSpinner({
  intent = 'inverse',
  size = 'xs',
  className,
}: ButtonSpinnerProps) {
  return (
    <span
      className={cn(spinnerVariants({ size, intent }), 'mr-2', className)}
      aria-hidden="true"
    />
  )
}

export { LoadingSpinner, spinnerVariants }
